<template>
  <div class="page-header">
    <h2>Gestion des Compagnies</h2>
    <button @click="openCreateModal" class="btn btn-primary">+ Nouvelle compagnie</button>
  </div>
  <!-- Messages d'info -->
  <InfoBox
    v-if="infoMessage"
    v-model:open="infoOpen"
    :type="infoType"
    @close="infoOpen = false; infoMessage = ''"
  >
    {{ infoMessage }}
  </InfoBox>
  <div class="filters form-group">
    <div class="btn-group">
      <div class="filters-group">
        <label for="countryFilter">Pays</label>
        <select v-model="countryFilter" class="filter-select">
          <option value="">Tous les pays</option>
          <option v-for="country in uniqueCountries" :key="country.id" :value="country.id">
            {{ country.name }}
          </option>
        </select>
      </div>
    </div>
    <div class="btn-group">
      <span class="badge badge-info">{{ filteredCompanies.length }} compagnie(s) trouvée(s) sur {{ allCompanies.length }}</span>
      <button @click="resetFilters" class="btn btn-secondary">Réinitialiser</button>
    </div>
  </div>
  <!-- Cards -->
  <div v-if="!loading && filteredCompanies.length > 0" class="card-group">
    <div
      v-for="company in filteredCompanies"
      :key="company.id"
      class="card card-admin"
      :class="{ 'is-modified': isModified(company.id) }"
    >
      <div class="card-content">
        <div class="form-group">
          <label>📷 Miniature</label>
          <div class="logo-upload">
            <div v-if="getLogoUrl(company.id)" class="logo-preview">
              <img :src="getLogoUrl(company.id)" :alt="`Miniature ${company.name}`" />
              <button
                @click="removeLogo(company.id)"
                class="btn btn-danger-secondary btn-sm"
                type="button"
              >
                🗑️
              </button>
            </div>
            <input
              type="file"
              accept="image/*"
              @change="handleLogoChange(company.id, $event)"
              :ref="`fileInput-${company.id}`"
            />
          </div>
        </div>
        <div class="form-group">
          <label>Nom de la compagnie</label>
          <input
            v-model="editForms[company.id].name"
            type="text"
            placeholder="Ex: Transports Dupont"
            @input="markAsModified(company.id)"
          />
        </div>
        <div class="form-group">
          <label>📍 Adresse</label>
          <select
            v-model="editForms[company.id].addressId"
            class="card-control"
            @change="markAsModified(company.id)"
          >
            <option value="">-- Sélectionner une adresse --</option>
            <option v-for="address in addresses" :key="address.id" :value="address.id">
              {{ address.name }} - {{ address.city?.name }}
            </option>
          </select>

          <div v-if="getSelectedAddress(company.id)" class="preview">
            <div class="preview__line">{{ getSelectedAddress(company.id).street }}</div>
            <div class="preview__line">
              {{ getSelectedAddress(company.id).postalCode }}
              {{ getSelectedAddress(company.id).city?.name }}
            </div>
            <div class="preview__line">{{ getSelectedAddress(company.id).country?.name }}</div>
            <div
              v-if="getSelectedAddress(company.id).complement"
              class="preview__line preview__muted"
            >
              {{ getSelectedAddress(company.id).complement }}
            </div>
          </div>
        </div>
      </div>

      <div class="card-footer">
        <button
          @click="saveCompany(company.id)"
          class="btn btn-save"
          :class="{ 'is-highlight': isModified(company.id) }"
          :disabled="!isModified(company.id)"
        >
          💾 Enregistrer
        </button>
        <button @click="openDeleteModal(company)" class="btn btn-danger-secondary">
          🗑 Supprimer
        </button>
      </div>
    </div>
  </div>

  <!-- État vide -->
  <EmptyState
    v-if="!loading && filteredCompanies.length === 0"
    :is-empty="true"
    emptyMessage="Aucun résultat trouvé"
  />
</template>

<script setup>
import { config } from '@/config/env.js'
import { ref, onMounted, reactive, computed } from 'vue'
import { useToast } from 'vue-toast-notification'
import { useModalStore } from '@/stores/modalStore'
import FormFieldsCompany from '@/components/FormFields/FormFieldsCompany.vue'
import InfoBox from '@/components/InfoBox.vue'
import EmptyState from '@/components/EmptyState.vue'

const emit = defineEmits(['loading'])

const toast = useToast()
const modalStore = useModalStore()

const allCompanies = ref([])
const addresses = ref([])
const loading = ref(false)
const logoFiles = reactive({})
const logoPreviews = reactive({})
const countryFilter = ref('')

const editForms = reactive({})
const modifiedCompanies = ref(new Set())

const infoMessage = ref('')
const infoType = ref('info')
const infoOpen = ref(false)

// 🎯 Computed: Liste unique des pays
const uniqueCountries = computed(() => {
  const countriesMap = new Map()

  allCompanies.value.forEach(company => {
    const country = company.address?.country
    if (country && !countriesMap.has(country.id)) {
      countriesMap.set(country.id, country)
    }
  })

  return Array.from(countriesMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  )
})

// 🎯 Computed: Compagnies filtrées par pays
const filteredCompanies = computed(() => {
  if (!countryFilter.value) {
    return allCompanies.value
  }

  return allCompanies.value.filter(company => {
    return company.address?.country?.id === countryFilter.value
  })
})

// 🎯 Fonction: Réinitialiser les filtres
const resetFilters = () => {
  countryFilter.value = ''
}

const initEditForm = (company) => {
  editForms[company.id] = {
    name: company.name || '',
    addressId: company.address?.id || '',
    logoName: company.logoName || '' // Toujours une string, jamais undefined
  }
  logoFiles[company.id] = null
  logoPreviews[company.id] = null
}

const markAsModified = (companyId) => {
  modifiedCompanies.value.add(companyId)
}

const isModified = (companyId) => {
  return modifiedCompanies.value.has(companyId)
}

const getSelectedAddress = (companyId) => {
  const addressId = editForms[companyId]?.addressId
  if (!addressId) return null
  return addresses.value.find(addr => addr.id === addressId)
}

onMounted(async () => {
  await Promise.all([loadCompanies(), loadAddresses()])
})

const loadCompanies = async () => {
  loading.value = true
  emit('loading', true)

  try {
    const res = await fetch(config.apiUrl + '/companies', {
      credentials: 'include'
    })

    if (res.ok) {
      allCompanies.value = await res.json()
      allCompanies.value.forEach(company => {
        initEditForm(company)
      })
    } else {
      showInfoMessage('error', 'Erreur lors du chargement des compagnies')
      toast.error('Erreur lors du chargement des compagnies')
    }
  } catch (err) {
    showInfoMessage('error', err?.message || 'Erreur de connexion')
    toast.error('Erreur de connexion')
    console.error(err)
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

const loadAddresses = async () => {
  try {
    const res = await fetch(config.apiUrl + '/addresses', {
      credentials: 'include'
    })

    if (res.ok) {
      addresses.value = await res.json()
    }
  } catch (err) {
    showInfoMessage('error', err?.message || 'Erreur lors du chargement des adresses')
    console.error('Erreur lors du chargement des adresses', err)
  }
}

const saveCompany = async (companyId) => {
  const form = editForms[companyId]

  if (!form.name) {
    toast.error('Le nom est requis')
    return
  }

  loading.value = true
  emit('loading', true)

  try {
    const company = allCompanies.value.find(c => c.id === companyId)
    const hadLogoInitially = company?.logoName
    const logoWasRemoved = !!(!form.logoName && !logoFiles[companyId] && hadLogoInitially)

    if (logoWasRemoved) {
      const deleteRes = await fetch(`${config.apiUrl}/company/${companyId}/logo`, {
        method: 'DELETE',
        credentials: 'include'
      })

      if (!deleteRes.ok) {
        const errorText = await deleteRes.text()
        showInfoMessage('error', 'Erreur lors de la suppression du logo')
        console.error('❌ Erreur lors de la suppression du logo:', errorText)
        toast.error('Erreur lors de la suppression du logo')
      } else {
        showInfoMessage('success', 'Logo supprimé avec succès')
        toast.success('Logo supprimé avec succès')
      }
    }

    const res = await fetch(`${config.apiUrl}/company/${companyId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({
        name: form.name,
        addressId: form.addressId || null
      })
    })

    if(!res.ok) {
      showInfoMessage('error', res?.message || 'Erreur lors de la modification')
      toast.error('Erreur lors de la modification')
      return
    }

    if(logoFiles[companyId]) {
      const formData = new FormData()
      formData.append('logo', logoFiles[companyId])

      const logoRes = await fetch(`${config.apiUrl}/company/${companyId}/logo`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      })

      if (!logoRes.ok) {
        showInfoMessage('warning', 'Compagnie modifiée, mais erreur lors du téléchargement du logo')
        toast.warning('Compagnie modifiée, mais erreur lors du téléchargement du logo')
      }
    }

    await loadCompanies()
    modifiedCompanies.value.delete(companyId)
    toast.success('Compagnie modifiée avec succès')
    showInfoMessage('success', 'Compagnie modifiée avec succès')
  } catch (err) {
    showInfoMessage('error', err?.message || 'Erreur de connexion')
    toast.error('Erreur de connexion')
    console.error(err)
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

// 🎯 CRÉATION
const openCreateModal = async () => {
  const { result } = modalStore.openComponent(
    FormFieldsCompany,
    {
      formData: { name: '', addressId: '' },
      addresses: addresses.value
    },
    {
      title: 'Créer une compagnie',
      maxWidth: '600px',
      kind: 'default'
    }
  )

  const res = await result
  if (res.action === 'submit') {
    await handleCreateCompany(res.data)
  }
}

const handleCreateCompany = async (formData) => {
  if (!formData.name) {
    toast.error('Le nom est requis')
    return
  }

  loading.value = true
  emit('loading', true)

  try {
    const res = await fetch(config.apiUrl + '/company', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({
        name: formData.name,
        addressId: formData.addressId || null
      })
    })

    if (res.ok) {
      await loadCompanies()
      showInfoMessage('success', 'Compagnie créée avec succès')
      toast.success('Compagnie créée avec succès')
    } else {
      showInfoMessage('error', 'Erreur lors de la création')
      toast.error('Erreur lors de la création')
    }
  } catch (err) {
    showInfoMessage('error', err?.message || 'Erreur de connexion')
    toast.error('Erreur de connexion')
    console.error(err)
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

// 🎯 SUPPRESSION
const openDeleteModal = async (company) => {
  const { result } = modalStore.open({
    kind: 'confirm',
    title: 'Confirmer la suppression',
    message: `Êtes-vous sûr de vouloir supprimer cette compagnie ?

Nom: ${company.name} - ${company.slug}

⚠️ Attention : Tous les véhicules associés à cette compagnie seront également affectés.`,
    confirmText: 'Supprimer',
    cancelText: 'Annuler'
  })

  const res = await result
  if (res.action === 'confirm') {
    await handleDeleteCompany(company.id)
  }
}

const handleDeleteCompany = async (companyId) => {
  loading.value = true
  emit('loading', true)

  try {
    const res = await fetch(`${config.apiUrl}/company/${companyId}`, {
      method: 'DELETE',
      credentials: 'include'
    })

    if (res.ok) {
      await loadCompanies()
      toast.success('Compagnie supprimée avec succès')
    } else {
      showInfoMessage('error', 'Erreur lors de la suppression')
      toast.error('Erreur lors de la suppression')
    }
  } catch (err) {
    showInfoMessage('error', err?.message || 'Erreur de connexion')
    toast.error('Erreur de connexion')
    console.error(err)
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

const handleLogoChange = (companyId, event) => {
  const file = event.target.files[0]

  if (!file) return

  if (!file.type.startsWith('image/')) {
    toast.error('Le fichier doit être une image')
    return
  }

  if (file.size > 5 * 1024 * 1024) {
    toast.error('L\'image ne doit pas dépasser 5 Mo')
    return
  }

  logoFiles[companyId] = file

  const reader = new FileReader()
  reader.onload = (e) => {
    logoPreviews[companyId] = e.target.result
  }
  reader.readAsDataURL(file)

  markAsModified(companyId)
}

// URL de la miniature (preview ou existante)
const getLogoUrl = (companyId) => {
  // Priorité à la preview si un nouveau fichier est sélectionné
  if (logoPreviews[companyId]) {
    return logoPreviews[companyId]
  }

  // Sinon, l'image existante
  const logoName = editForms[companyId]?.logoName
  if (logoName) {
    return `${config.apiUrl}/uploads/logos/${logoName}`
  }

  return null
}

// Supprime la miniature
const removeLogo = (companyId) => {
  logoFiles[companyId] = null
  logoPreviews[companyId] = null
  editForms[companyId].logoName = ''
  markAsModified(companyId)
}


const showInfoMessage = (type = 'info', message) => {
  infoMessage.value = message
  infoType.value = type
  infoOpen.value = true
}
</script>
